import React, { useEffect, useState, useContext } from "react";
import Modal from "@mui/material/Modal";
import Swal from "sweetalert2";
import ServicableAreaApi from "../../../../API/ServicableAreaApi";
import OrgApi from "../../../../API/OrgApi";
import UsersApi from "../../../../API/UsersApi";
import SearchOrgReq from "../../../../Requests/Organizations/SearchOrgReq";
import SearchUserReq from "../../../../Requests/Users/SearchUserReq";
import { IoMdCloseCircle } from "react-icons/io"; // Import close icon
import { UserContext } from "../../../../App";

const ShareLabelModal = ({ open, setOpen, label }) => {
  const [salesOrgs, setSalesOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [salesReps, setSalesReps] = useState([]);
  const [selectedRep, setSelectedRep] = useState(null);
  const User = useContext(UserContext);

  useEffect(() => {
    if (open) {
      resetSelections();
      fetchSalesOrgs();
    }
  }, [open]);

  const resetSelections = () => {
    setSelectedOrg(null);
    setSelectedRep(null);
    setSalesReps([]);
  };

  const fetchSalesOrgs = async () => {
    let searchReq = new SearchOrgReq();
    const res = await OrgApi.SearchOrg(searchReq);

    if (res?.status?.success) {
      let orgOptions = res?.data?.list?.map((item) => ({
        value: item.id,
        label: item.name,
      }));

      if (User.userType === "SalesOrgAdmin") {
        orgOptions = orgOptions.filter((org) => org.value === User.salesOrgId);
        setSelectedOrg(User.salesOrgId);
        fetchSalesReps(User.salesOrgId);
      }

      setSalesOrgs(orgOptions);
    } else {
      Swal.fire("Error", "Failed to load sales orgs", "error");
      setSalesOrgs([]);
    }
  };

  const fetchSalesReps = async (orgId) => {
    setSelectedOrg(orgId);
    setSelectedRep(null);

    let req = new SearchUserReq();
    req.salesOrgId = orgId;
    req.userTypes = ["SalesRep", "SalesOrgAdmin"];
    req.pagingParams = { pageNumber: 1, pageSize: 20 };

    const res = await UsersApi.SearchUser(req);

    if (res?.status?.success) {
      let repOptions = res?.data?.list?.map((user) => ({
        value: user.id,
        label: `${user.firstName} ${user.lastName}`,
      }));
      setSalesReps(repOptions);
    } else {
      Swal.fire("Error", "Failed to load sales reps", "error");
      setSalesReps([]);
    }
  };

  const handleClose = () => {
    setOpen(false);
    resetSelections();
  };

  const handleShare = async () => {
    if (!selectedRep) {
      Swal.fire("Error", "Please select a Sales Rep", "error");
      return;
    }

    if (User.userType === "SalesRep") {
      Swal.fire("Error", "Sales Reps cannot share labels", "error");
      return;
    }

    const req = {
      LabelId: label.id,
      SalesRepId: selectedRep,
      AssignedById: User.id,
    };

    const res = await ServicableAreaApi.ShareLabelToSalesRep(req);

    if (res?.status?.success) {
      Swal.fire("Success", "Label shared successfully!", "success");
      handleClose();
    } else {
      Swal.fire(
        "Error",
        res?.status?.message || "Failed to share label",
        "error"
      );
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
  <div className="fixed inset-0 flex items-center justify-center bg-transparent bg-opacity-50 z-50">
  <div className="p-6 bg-white shadow-lg rounded-lg w-[90%] max-w-[450px] mx-auto relative border">       
     {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
        >
          <IoMdCloseCircle size={24} />
        </button>

        {/* Logo */}
        <div className="flex justify-center mb-4">
          <img
            alt="Logo"
            src={"/images/nav/nav-logo.png"}
            className="object-contain max-w-[150px] h-auto"
          />
        </div>

        {/* Title */}
        <h2 className="text-xl font-bold text-center text-gray-800 mb-4">
          Share Label
        </h2>

        {/* Sales Org Dropdown */}
        <label className="block text-sm font-medium text-gray-600">
          Select Sales Org
        </label>
        <select
          value={selectedOrg || ""}
          onChange={(e) => {
            if (User.userType !== "SalesOrgAdmin") {
              const orgId = e.target.value;
              setSelectedOrg(orgId);
              fetchSalesReps(orgId);
            }
          }}
          disabled={User.userType === "SalesOrgAdmin"}
          className="w-full p-2 border rounded mb-3 overflow-y-auto"
        >
          <option value="" disabled>
            Select Sales Org
          </option>
          {salesOrgs.length > 0 ? (
            salesOrgs.map((org, index) => (
              <option key={index} value={org.value}>
                {org.label}
              </option>
            ))
          ) : (
            <option disabled>Loading...</option>
          )}
        </select>

        {/* Sales Rep Dropdown */}
        {salesReps.length > 0 && (
          <>
            <label className="block text-sm font-medium text-gray-600">
              Select Sales Rep
            </label>
            <select
              className="w-full p-2 border rounded mb-3 overflow-y-auto"
              value={selectedRep || ""}
              onChange={(e) => setSelectedRep(e.target.value)}
            >
              <option value="">Select Rep</option>
              {salesReps.map((rep) => (
                <option key={rep.value} value={rep.value}>
                  {rep.label}
                </option>
              ))}
            </select>
          </>
        )}

        {/* Share Button */}
        <button
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 w-full"
          onClick={handleShare}
          disabled={!selectedRep}
        >
          Share Label
        </button>
      </div>
    </div>
    </Modal>
  );
};

export default ShareLabelModal;
